import { RECOMMENDATION_TYPE } from '@/types';
import { AUDIENCE_CSV_IMPORT_TYPE } from './audiences';

const IOT_GPT_API = {
  chatQuery: '/private/chats/process-query',
  chatQueryDev: '/private/chats/process-query-dev',
  getUserThreads: '/private/chats/get-user-threads',
  getChatThreadDetails: (threadId: string) => `/private/chats/get-thread-details/${threadId}`,
  getRecommendationProductDetail: (query?: string | number, recommendationType?: string) =>
    recommendationType === RECOMMENDATION_TYPE.DEVICES
      ? `/public/devices/${query}`
      : `/public/products/${query}`,
  deleteThread: (threadId: string) => `/private/chats/thread/${threadId}`,
  greetingsFeedback: '/public/chatbot/feedback',
  publicChatQueryDev: '/public/chats/process-query-dev',
};

const PRODUCT_API = {
  queryDevice: '/public/chats/queryDevice',
  getVerifiedAlternateProducts: (productId: number) => `/public/products/${productId}/verified-alternate-products`,
};

const PROJECTS_API = {
  getAllProjectsByUser: '/private/projects/',
  createNewProject: '/private/projects/',
  addProductToProject: (projectId: string | number) => `/private/projects/${projectId}/products`,
  checkProductSaved: (productId: string | number) => `/private/projects/check-product/${productId}`,
  getAllSavedProducts: '/private/projects/user/saved-products',
  removeProductFromProject: (projectId: number, productId: number) =>
    `/private/projects/${projectId}/products/${productId}`,
  getAllProjects: '/private/projects/',
};

const SUBSCRIPTION_API = {
  currentSubscription: '/private/subscription/current',
  moduleSubscriptions: '/private/subscription/modules',
  subscriptionHistory: '/private/subscription/history',
  createSubscription: '/private/subscription/create',
  calculatePricing: '/public/pricing/calculate',
  creditTopup: '/private/subscription/credits/topup',
  aiCreditTopup: '/private/subscription/credits/ai-topup',
  enrichmentUsage: '/private/subscription/enrichment-usage',
  autoTopup: '/private/subscription/credits/auto-topup',
  aiAutoTopup: '/private/subscription/credits/ai-auto-topup',
  avatarCreditTopup: '/private/subscription/credits/avatar-topup',
  avatarAutoTopup: '/private/subscription/credits/avatar-auto-topup',
  billingPortal: '/private/subscription/billing-portal',
  planChange: '/private/subscription/plan-change',
  cancelSubscription: '/private/subscription/cancel',
  reactivateSubscription: '/private/subscription/reactivate',
  // Agent Payments (Stripe Issuing)
  agentPaymentsBalance: '/private/agent-payments/balance',
  agentPaymentsTransactions: '/private/agent-payments/transactions',
  agentPaymentsTopup: '/private/agent-payments/topup',
  agentPaymentsLimit: '/private/agent-payments/limit',
};

const QUOTES_API = {
  createQuotes: '/private/quotes',
};

const VENDOR_API = {
  inviteUser: '/private/users/invite',
  deleteUser: (userId: string) => `/private/users/${userId}`,
  getAssociatedVendorUsers: '/private/users/associated',
  updateLeadEmails: '/private/vendors/update-lead-emails',
  getVendorSettings: '/private/vendors/email-settings',
  uploadVendorFile: '/private/links/upload-file',
};

const VOICE_API = {
  getVoices: '/private/voice/voices',
  getFlags: '/private/voice/flags',
  getInitialVoiceAgentData: '/private/voice/voice-agent',
  getAmbientSounds: '/private/voice/ambient-sounds',
  createVoiceAgent: '/private/voice/create-voice-agent',
  updateVoiceAgent: (agent_id?: string) => `/private/voice/update-voice-agent/${agent_id}`,
  toggleVoiceAgent: (agent_id?: string) => `/private/voice/toggle-voice-agent/${agent_id}`,
  getCallHistory: '/private/voice/calls',
  createPhoneCall: '/private/voice/create-phone-call',
};

const ANALYTICS_API = {
  getTrafficAnalytics: '/private/analytics/traffic',
  getCampaignPerformance: '/private/analytics/campaign-performance',
  getCampaignDetails: '/private/analytics/campaign-details',
  getCampaignFilterOptions: '/private/analytics/campaign-filter-options',
  getLatestVisitUniqueUsers: '/private/analytics/latest-visit-unique-users',
  getVisitUserDetails: '/private/analytics/visit-user-details',
  toggleIgnoreCustomer: '/private/analytics/toggle-ignore-customer',
  // mb-server#6451: the manual override in the shared customer definition.
  customerMark: '/private/analytics/customer-mark',
  customerMarks: '/private/analytics/customer-marks',
  promoteSignalToTask: '/private/analytics/signals/promote-to-task',
  promoteSignalsToTasksBulk: '/private/analytics/signals/promote-to-tasks-bulk',
  // One-tap Add to outreach (mb-frontend#4638): the enrollment intent resource.
  signalEnrollments: '/private/signal-enrollments',
  signalEnrollmentPreview: '/private/signal-enrollments/preview',
  signalEnrollmentReadiness: '/private/signal-enrollments/readiness',
  signalEnrollment: (enrollmentId: string) => `/private/signal-enrollments/${encodeURIComponent(enrollmentId)}`,
  signalEnrollmentCancel: (enrollmentId: string) => `/private/signal-enrollments/${encodeURIComponent(enrollmentId)}/cancel`,
  signalEnrollmentRetry: (enrollmentId: string) => `/private/signal-enrollments/${encodeURIComponent(enrollmentId)}/retry`,
  // Campaigns > Templates (mb-frontend#4638 phase 2). Distinct from any DM/message-snippet
  // template resource — this is the multi-step outreach-sequence template CRUD.
  outreachTemplates: '/private/outreach-templates',
  outreachTemplateRoster: '/private/outreach-templates/roster',
  outreachTemplateVariables: '/private/outreach-templates/variables',
  outreachTemplate: (templateId: number | string) => `/private/outreach-templates/${encodeURIComponent(templateId)}`,
  outreachTemplatePreview: (templateId: number | string) => `/private/outreach-templates/${encodeURIComponent(templateId)}/preview`,
  outreachTemplatePreviewSample: (templateId: number | string) => `/private/outreach-templates/${encodeURIComponent(templateId)}/preview/sample`,
  getUnifiedPeople: '/private/analytics/signals-v2/people',
  getSignalsFilterOptions: '/private/analytics/filter-options',
  getSignalsFilterPageUrls: '/private/analytics/filter-options/page-urls',
  namedAccountBulk: '/private/analytics/named-accounts/bulk',
  getNamedAccounts: '/private/analytics/named-accounts',
  deleteNamedAccounts: `/private/analytics/named-accounts/all`,
  excludedCustomerBulk: '/private/analytics/ignored-customers/bulk',
  getExcludedCustomers: '/private/analytics/ignored-customers',
  deleteExcludedCustomers: '/private/analytics/ignored-customers/all',
  getCampaignImpactData: '/private/analytics/campaign-impact',
  getCompanyInfluencingCampaigns: '/private/analytics/company-campaigns',
  getCampaignLandingPageStats: '/private/analytics/campaign-landing-stats',
  getVectorContacts: '/private/analytics/vector-contacts',
  getVectorContactsDailyStats: '/private/analytics/vector-contacts/daily-stats',
  getVectorContactFilterOptions: '/private/analytics/vector-contacts/filter-options',
  // Unified Signals endpoint - combines all signal sources
  getUnifiedSignals: '/private/analytics/signals-v2',
  exportUnifiedSignals: '/private/analytics/signals-v2/export',
  // Find people by persona at a company (Demandbase → Exa waterfall)
  findPeopleByPersona: (companyDomain: string) => `/private/analytics/signals/company/${companyDomain}/people`,
  // On-demand contact enrichment via PersonFinder
  findContacts: '/private/persona/find-contacts',
  findContactsResult: (requestId: string) => `/private/persona/find-contacts/${requestId}`,
  crmMatchContact: '/private/persona/crm-match',
  // Prior external outreach (HubSpot/Salesforce/manual) sent to a prospect outside MarketBetter
  getPersonaPriorOutreach: '/private/persona/prior-outreach',
  // Get company execution history (contact-level workflow executions)
  getCompanyExecutionHistory: (vendorId: number, companyDomain: string) => `/private/analytics/company-execution-history/${vendorId}/${companyDomain}`,
  // Get unified company journey - 360° activity view
  getCompanyJourney: '/private/analytics/company-journey',
  getAccountRoster: '/private/analytics/account-roster',
  // One person's website visits and chatbot conversations, by email (mb-server#6321)
  getPersonActivity: '/private/analytics/person-activity',
  getContactEmails: '/private/analytics/contact-emails',
  getEmailWorkflows: '/private/analytics/email-workflows',
  getEmailWorkflowPlays: (audienceId: string) => `/private/analytics/email-workflows/${audienceId}/plays`,
  getEmailWorkflowReplyInsights: (audienceId: string | number) => `/private/analytics/email-workflows/${audienceId}/reply-insights`,
  getAllEmailCampaigns: '/private/analytics/all-email-campaigns',
  getAllLinkedInCampaigns: '/private/analytics/all-linkedin-campaigns',
  getAllCampaignsReplyInsights: '/private/analytics/all-campaigns-reply-insights',
  getAnalyticsFilterOptions: '/private/analytics/workflow-filter-options',
  getOutreachWorkflows: '/private/analytics/outreach-workflows',
  getLinkedInWorkflows: '/private/analytics/linkedin-workflows',
  // New campaign results endpoints (workspace-wide)
  getCampaignEmailResults: '/private/campaigns/email/results',
  getCampaignOutreachResults: '/private/campaigns/outreach/results',
  getCampaignHeyReachResults: '/private/campaigns/heyreach/results',
  getLinkedInRepliedThreads: '/private/campaigns/heyreach/replied-threads',
  sendLinkedInReply: '/private/campaigns/heyreach/send-reply',
  // Multi-workflow analytics endpoints
  getWorkflowSummary: (vendorId: string) => `/private/analytics/workflow-summary/${vendorId}`,
  getOutreachSummary: (vendorId: string) => `/private/analytics/outreach-summary/${vendorId}`,
  getLinkedInSummary: (vendorId: string) => `/private/analytics/linkedin-summary/${vendorId}`,
  // Background Exports
  exportEmailSendHistory: '/private/analytics/email-send-history',
};

export const PROSPECTS_API = {
  getProspects: '/private/prospects',
  getProspectDetail: '/private/prospects/detail',
  getProspectAccountSnapshot: '/private/prospects/detail/account-snapshot',
  createContact: '/private/prospects',
  enrichContact: '/private/prospects/enrich',
  doNotContact: '/private/prospects/do-not-contact',
};

export const OPPORTUNITIES_API = {
  getOpportunities: '/private/opportunities',
  getCounts: '/private/opportunities/counts',
  getAeWorklist: '/private/opportunities/ae-worklist',
  getSuggestions: '/private/opportunities/suggestions',
  // Generic feature-request endpoints introduced with the opportunities upgrade flow
  // (BE PR #1434). The BE keeps the legacy /sync-interest paths aliased
  // for backward compatibility, but new code should prefer these.
  getFeatureRequest: '/private/opportunities/feature-request',
  recordFeatureRequest: '/private/opportunities/feature-request',
  // Legacy aliases — kept so existing call sites keep working until they
  // migrate to the generic endpoints above.
  getSyncInterest: '/private/opportunities/sync-interest',
  recordSyncInterest: '/private/opportunities/sync-interest',
  getPipelineSummary: '/private/opportunities/pipeline-summary',
  getPipelineGrouped: '/private/opportunities/pipeline-grouped',
  saveCardOrder: '/private/opportunities/card-order',
  saveStageOrder: '/private/opportunities/stage-order',
  createOpportunity: '/private/opportunities',
  getOpportunityDetail: (id: string) => `/private/opportunities/${id}`,
  getOpportunityActivity: (id: string) => `/private/opportunities/${id}/activity`,
  listOpportunityContacts: (id: string) => `/private/opportunities/${id}/contacts`,
  addOpportunityContact: (id: string) => `/private/opportunities/${id}/contacts`,
  removeOpportunityContact: (id: string, personId: number) => `/private/opportunities/${id}/contacts/${personId}`,
  deleteOpportunity: (id: string) => `/private/opportunities/${id}`,
  updateOpportunity: (id: string) => `/private/opportunities/${id}`,
  updateOpportunityStage: (id: string) => `/private/opportunities/${id}/stage`,
  closeOpportunity: (id: string) => `/private/opportunities/${id}/close`,
  // GH #4205 — Follow-up intelligence engine, Slice 1 (To-do lane + composer).
  getFollowupLane: '/private/opportunities/followups/lane',
  getFollowupSettings: '/private/opportunities/followups/settings',
  updateFollowupSettings: '/private/opportunities/followups/settings',
  getFollowupComposerContext: (id: string) => `/private/opportunities/${id}/followup`,
  sendFollowup: (id: string) => `/private/opportunities/${id}/followup/send`,
  markFollowupComplete: (id: string) => `/private/opportunities/${id}/followup/complete`,
  scheduleFollowupReminder: (id: string) => `/private/opportunities/${id}/followup/schedule`,
  cancelFollowupReminder: (id: string) => `/private/opportunities/${id}/followup/schedule`,
  // "Don't remind me again" (POST) and un-mute (DELETE).
  dismissFollowup: (id: string) => `/private/opportunities/${id}/followup/dismiss`,
  addManualFollowup: (id: string) => `/private/opportunities/${id}/followup/manual`,
};

export const DASHBOARD_API = {
  getLeadsActivity: '/private/dashboard/leads-activity',
  getMarketingSnapshot: '/private/dashboard/marketing-snapshot',
  getSalesSnapshot: '/private/dashboard/sales-snapshot',
  getOutboundInfluencedInbound: '/private/dashboard/outbound-influenced-inbound',
  getRevenueImpact: '/private/analytics/revenue-impact',
  getSignalEffectiveness: '/private/analytics/signal-effectiveness',
  getTeamActivity: '/private/analytics/team-activity',
  getAdminTodaySummary: '/private/analytics/today/admin-summary',
  getMarketingTrafficOverview: '/private/analytics/marketing-traffic-overview',
};

export const BOT_API = {
  getAllBotsData: (vendorId: number) => `/private/chatbot/${vendorId}`,
  getInternalAgent: '/private/chatbot/internal-agent',
  getInternalAgentSummary: (accessToken: string) =>
    `/private/chatbot/internal-agent-summary/${accessToken}`,
  upsertEntry: '/private/chatbot/training-data/replace',
  getEntries: (chatToken: string) => `/private/chatbot/training-data/${chatToken}`,
  createDefaultBot: '/api/internal/default-bot',
  improveAnswer: '/private/chatbot/training-data',
  getConversation: (threadId: string) => `/private/chatbot/conversation/${threadId}`,
  getContact: (threadId: string) => `/private/chatbot/contact/${threadId}`,
  updateContact: (threadId: string) => `/private/chatbot/contact/${threadId}`,
  inboxStatus: (threadId: string) => `/private/chatbot/threads/${threadId}/inbox-status`,
  joinThread: (threadId: string) => `/private/chatbot/threads/${threadId}/join`,
  sendSDRMessage: (threadId: string) => `/private/chatbot/threads/${threadId}/message`,
  leaveThread: (threadId: string) => `/private/chatbot/threads/${threadId}/leave`,
  requestForm: (threadId: string) => `/private/chatbot/threads/${threadId}/request-form`,
  inboxDisposition: (threadId: string) => `/private/chatbot/threads/${threadId}/inbox-disposition`,
  spamDisposition: (threadId: string) => `/private/chatbot/threads/${threadId}/spam-disposition`,
  getWhispers: (threadId: string) => `/private/chatbot/threads/${threadId}/whispers`,
  createWhisper: (threadId: string) => `/private/chatbot/threads/${threadId}/whispers`,
  pingWatcherPresence: (threadId: string) => `/private/chatbot/threads/${threadId}/watchers/ping`,
  removeWatcherPresence: (threadId: string) => `/private/chatbot/threads/${threadId}/watchers`,
};

export const INTEGRATIONS_CONTACT_API = {
  getSalesforceContact: (email: string) => `/private/integrations/salesforce/contact/${email}`,
  getHubSpotContact: (email: string) => `/private/integrations/hubspot/contact/${email}`,
  // Add a person to the connected CRM. Path matches the Express mount (/private/integration, singular).
  addSalesforceContact: '/private/integration/salesforce/contact',
  addHubSpotContact: '/private/integration/hubspot/contact',
  // Add the ACCOUNT on its own, for a row that carries no contact email.
  addHubSpotCompany: '/private/integration/hubspot/company',
};

// #4544 Phase 3: Pounce notification policy CRUD (mb-server#4554). Named distinctly from the
// existing, unrelated "Pounce" (Smart Scheduler) endpoints elsewhere in this file.
export const NOTIFICATION_POLICIES_API = {
  createNotificationPolicy: '/private/notification-policies',
  getNotificationPolicies: '/private/notification-policies',
  getNotificationPolicyRecipients: '/private/notification-policies/recipients',
  getNotificationPolicy: (id: number) => `/private/notification-policies/${id}`,
  updateNotificationPolicy: (id: number) => `/private/notification-policies/${id}`,
  deleteNotificationPolicy: (id: number) => `/private/notification-policies/${id}`,
  setNotificationPolicyChannels: (id: number) => `/private/notification-policies/${id}/channels`,
  sendNotificationPolicyTest: (id: number) => `/private/notification-policies/${id}/test`,
  getNotificationPolicyRecentMatches: (id: number) => `/private/notification-policies/${id}/recent-matches`,
};

const AUDIENCES_API = {
  getAllFacts: '/private/audience/facts',
  createAudience: '/private/audience',
  getAllAudiences: '/private/audience',
  getSkills: '/private/skills', // #3884 Chat-with-MB skills catalog
  getMySkills: '/private/skills/mine', // #3926 workspace-authored skills
  draftSkill: '/private/skills/draft', // #3926 AI-assist
  skillBySlug: (slug: string) => `/private/skills/${slug}`, // #3926 edit/delete
  deleteAudience: (id: number) => `/private/audience/${id}`,
  updateAudience: (id: number) => `/private/audience/${id}`,
  addAudienceToCampaign: (id: number) => `/private/audience/${id}/add-to-campaign`, // #3884
  searchAudiences: '/private/audience/search',
  uploadCsvForAudience: '/private/audience/upload-csv',
  importWebsiteVisits: '/private/audience/import-website-visits',
  websiteVisitTaskPeople: '/private/audience/website-visits/task-people',
  getAudienceCsvOrImagesDataUploaded: (type: AUDIENCE_CSV_IMPORT_TYPE, list_id: number) =>
    `/private/audience/${type}/${list_id}`,
  getAudienceAttributeColumns: (type: AUDIENCE_CSV_IMPORT_TYPE, list_id: number) =>
    `/private/audience/${type}/${list_id}/attribute-columns`,
  getAudienceRowsByIds: (type: AUDIENCE_CSV_IMPORT_TYPE, list_id: number) =>
    `/private/audience/${type}/${list_id}/by-ids`,
  bulkDeleteRows: (type: AUDIENCE_CSV_IMPORT_TYPE, list_id: number) =>
    `/private/audience/${type}/${list_id}/bulk-delete`,
  getEmptyRows: (type: AUDIENCE_CSV_IMPORT_TYPE, list_id: number, field: string) =>
    `/private/audience/${type}/${list_id}/empty-rows/${field}`,
  // Multi-provider re-enrichment: row-scope + per-provider eligibility counts for the
  // column ⋯ menu, and the exact rows a provider run should dispatch.
  getEnrichSummary: (type: AUDIENCE_CSV_IMPORT_TYPE, list_id: number, field: string) =>
    `/private/audience/${type}/${list_id}/enrich-summary/${field}`,
  // POST: starting a run CLAIMS the rows it returns, so it is not a safe read.
  startEnrichRun: (type: AUDIENCE_CSV_IMPORT_TYPE, list_id: number, field: string) =>
    `/private/audience/${type}/${list_id}/enrich-run/${field}`,
  releaseEnrichClaims: (type: AUDIENCE_CSV_IMPORT_TYPE, list_id: number, field: string) =>
    `/private/audience/${type}/${list_id}/enrich-release/${field}`,
  getEnrichOutcomes: (type: AUDIENCE_CSV_IMPORT_TYPE, list_id: number, field: string) =>
    `/private/audience/${type}/${list_id}/enrich-outcomes/${field}`,
  // mb-server#5493: how many of the list's contacts have opted out of email, split by
  // which system holds the opt-out. Read before a launch, to size the run honestly.
  getEmailSuppressionSummary: (type: AUDIENCE_CSV_IMPORT_TYPE, list_id: number) =>
    `/private/audience/${type}/${list_id}/email-suppression-summary`,
  deleteAllRowsByListId: (type: AUDIENCE_CSV_IMPORT_TYPE, list_id: number) =>
    `/private/audience/${type}/${list_id}`,
  getRowById: (type: AUDIENCE_CSV_IMPORT_TYPE, list_id: number, id: number) =>
    `/private/audience/${type}/${list_id}/${id}`,
  updateRowById: (type: AUDIENCE_CSV_IMPORT_TYPE, list_id: number, id: number) =>
    `/private/audience/${type}/${list_id}/${id}`,
  insertSingleRow: (type: AUDIENCE_CSV_IMPORT_TYPE, list_id: number) =>
    `/private/audience/${type}/${list_id}/row`,
  deleteRowById: (type: AUDIENCE_CSV_IMPORT_TYPE, list_id: number, id: number) =>
    `/private/audience/${type}/${list_id}/${id}`,
  getAudienceById: (id: number) => `/private/audience/${id}`,
  suggestAudienceSummary: (audienceId: number) => `/private/audience/${audienceId}/summary`,
  suggestMotion: (audienceId: number) => `/private/audience/${audienceId}/motion-suggestion`,
  setCampaignMotion: (audienceId: number) => `/private/audience/${audienceId}/motion`,
  suggestFocus: (audienceId: number) => `/private/audience/${audienceId}/focus-suggestion`,
  suggestGoals: (audienceId: number) => `/private/audience/${audienceId}/goal-suggestions`,
  suggestTargeting: (audienceId: number) => `/private/audience/${audienceId}/targeting-suggestions`,
  hasPhoneNumbers: (audienceId: number) => `/private/audience/${audienceId}/has-phone-numbers`,
  getAudienceTags: '/private/audience/tags',
  getRecommendedAudiences: '/private/audience/recommended',
  duplicateAudience: (audienceId: number) => `/private/audience/${audienceId}/duplicate`,
  runNow: (audienceId: number) => `/private/audience/${audienceId}/run-now`,
  crmEnrich: (audienceId: number) => `/private/audience/${audienceId}/crm-enrich`,
  clearCampaignAttention: (audienceId: number) => `/private/audience/${audienceId}/attention-clear`,
  salesforceCompanyListImport: (audienceId: number) => `/private/audience/${audienceId}/salesforce-company-list`,
  linkedinEnrichmentBatches: (audienceId: number) =>
    `/private/audience/${audienceId}/linkedin-enrichment/batches`,
  linkedinEnrichmentBatch: (audienceId: number, batchId: string) =>
    `/private/audience/${audienceId}/linkedin-enrichment/batches/${batchId}`,
  emailEnrichmentBatches: (audienceId: number) =>
    `/private/audience/${audienceId}/email-enrichment/batches`,
  emailEnrichmentBatch: (audienceId: number, batchId: string) =>
    `/private/audience/${audienceId}/email-enrichment/batches/${batchId}`,
  // Audience webhook trigger
  audienceWebhookProvision: (id: number) => `/private/audience/${id}/webhook/provision`,
  audienceWebhookRegenerateSecret: (id: number) => `/private/audience/${id}/webhook/regenerate-secret`,
  audienceWebhookMapping: (id: number) => `/private/audience/${id}/webhook/mapping`,
  audienceWebhookLatestPayload: (id: number) => `/private/audience/${id}/webhook/latest-payload`,
  audienceWebhookAiMappingPreview: (id: number) => `/private/audience/${id}/webhook/ai-mapping-preview`,
  audienceWebhook: (id: number) => `/private/audience/${id}/webhook`,
  audienceWebhookFilter: (id: number) => `/private/audience/${id}/webhook/filter`,
  audienceWebhookDetectedFields: (id: number) => `/private/audience/${id}/webhook/detected-fields`,
  audienceWebhookActivity: (id: number) => `/private/audience/${id}/webhook/activity`,
  // Audience Research Columns — gated by AI_RESEARCH_COLUMNS server-side flag.
  // 404 from any of these means the flag is off; the UI hides itself silently.
  researchColumns: (audienceId: number) =>
    `/private/audience/${audienceId}/research-columns`,
  researchEngineStatus: (audienceId: number) =>
    `/private/audience/${audienceId}/research-columns/engine-status`,
  researchSourceAttributes: (audienceId: number) =>
    `/private/audience/${audienceId}/research-columns/source-attributes`,
  // #4360: TTS providers + curated voices for the voicemail column's voice picker.
  researchVoiceOptions: (audienceId: number) =>
    `/private/audience/${audienceId}/research-columns/voice-options`,
  researchColumn: (audienceId: number, colId: number) =>
    `/private/audience/${audienceId}/research-columns/${colId}`,
  researchColumnRun: (audienceId: number, colId: number) =>
    `/private/audience/${audienceId}/research-columns/${colId}/run`,
  researchColumnRowRun: (audienceId: number, colId: number, rowId: number) =>
    `/private/audience/${audienceId}/research-columns/${colId}/rows/${rowId}/run`,
  researchColumnCells: (audienceId: number, colId: number) =>
    `/private/audience/${audienceId}/research-columns/${colId}/cells`,
  // AI field research — fills a built-in column (job_title/company_name/
  // industry/website) in-place for empty rows.
  fieldResearchRun: (audienceId: number) =>
    `/private/audience/${audienceId}/field-research/run`,
  researchSuggestName: (audienceId: number) =>
    `/private/audience/${audienceId}/research-columns/suggest-name`,
  // Find people by title on a company audience → fill company.contact_* fields.
  audienceFindPeople: (audienceId: number) =>
    `/private/audience/${audienceId}/find-people`,
  audienceRepairFindPeopleMismatches: (audienceId: number) =>
    `/private/audience/${audienceId}/find-people/repair-mismatches`,
  // Progress of the latest find-people run (polled while a run is active, #3840).
  audienceFindPeopleStatus: (audienceId: number) =>
    `/private/audience/${audienceId}/find-people/status`,
  testProcessing: '/private/audience/test-processing',
  getProcessedRecords: '/private/audience/processed-records',
  getCountries: '/private/audience/countries',
  previewHubSpotContacts: '/private/integration/hubspot/preview',
  // HubSpot Lists
  getHubSpotLists: '/private/integration/hubspot/lists',
  getHubSpotListContacts: (listId: number | string) =>
    `/private/integration/hubspot/lists/${listId}/contacts`,
  importHubSpotListContacts: (listId: number | string) =>
    `/private/integration/hubspot/lists/${listId}/import`,
  addContactsToHubSpotList: (listId: number | string) =>
    `/private/integration/hubspot/lists/${listId}/contacts/add`,
  getAudienceCountries: '/private/audience/countries',
  getAudienceContinents: '/private/audience/continents',
  getAudienceStates: '/private/audience/states',
  getAvailableWorkflows: '/private/plays/copy/available-workflows',
  copyWorkflow: '/private/plays/copy/actions',
  copyWorkflowWithAudience: '/private/plays/copy/workflow-with-audience',
  copyTemplateToAudience: '/private/plays/copy/template-to-audience',
  checkCustomerMatches: (type: AUDIENCE_CSV_IMPORT_TYPE, list_id: number) =>
    `/private/audience/${type}/${list_id}/check-customers`,
  checkCrossAudienceOverlap: (type: AUDIENCE_CSV_IMPORT_TYPE, list_id: number) =>
    `/private/audience/${type}/${list_id}/check-cross-audience`,
  checkOutreachStatus: (audienceId: number) =>
    `/private/audience/${audienceId}/outreach-status`,
  getAudienceMatches: (audienceId: number) =>
    `/private/audience/${audienceId}/matches`,
  runAccountFit: (audienceId: number) =>
    `/private/audience/${audienceId}/account-fit/run`,
  getAccountFitStatus: (audienceId: number) =>
    `/private/audience/${audienceId}/account-fit/status`,
  getAccountFitEstimate: (audienceId: number) =>
    `/private/audience/${audienceId}/account-fit/estimate`,
  previewLinkedInCommenters: '/private/audience/preview-linkedin-commenters',
  importLinkedInCommenters: '/private/audience/import-linkedin-commenters',
  previewNonResponders: (audienceId: number) => `/private/audience/${audienceId}/non-responders`,
  importNonResponders: (audienceId: number) => `/private/audience/${audienceId}/non-responders/import`,
  suppressContact: (audienceId: number) => `/private/audience/${audienceId}/contacts/suppress`,
  getSuppressedContacts: (audienceId: number) => `/private/audience/${audienceId}/contacts/suppressed`,
  unsuppressContact: (audienceId: number, suppressionId: number) => `/private/audience/${audienceId}/contacts/suppress/${suppressionId}`,
  // TAM Builder (Fiber NL company search)
  createTamSearch: '/private/tam/search',
  getTamSearch: (searchId: number) => `/private/tam/search/${searchId}`,
};

export const PLAYS_API = {
  getAllPlays: '/private/plays',
  getPlayListings: '/private/plays/listings',
  storeAudiencePlay: '/private/plays',
  getPlayDetails: (id: number) => `/private/plays/details/${id}`,
  getPlaysByAudienceId: (audienceId: number) => `/private/plays/audience/${audienceId}`,
  removeAudiencePlay: (id: number) => `/private/plays/${id}`,
  moveAudiencePlay: (id: number) => `/private/plays/${id}/move`,
  patchPlayNode: (nodeId: number) => `/private/plays/nodes/${nodeId}`,
  skipPlay: (audiencePlayId: number) => `/private/plays/${audiencePlayId}/skip`,
  suggestSequencePlan: (audienceId: number) => `/private/plays/audience/${audienceId}/sequence-suggestion`,
  assembleSequence: (audienceId: number) => `/private/plays/audience/${audienceId}/sequence`,
  startSequenceBuild: (audienceId: number) => `/private/plays/audience/${audienceId}/sequence/jobs`,
  sequenceBuild: (audienceId: number, jobId: string) => `/private/plays/audience/${audienceId}/sequence/jobs/${jobId}`,
  previewSequence: (audienceId: number) => `/private/plays/audience/${audienceId}/sequence/preview`,
  suggestNameTags: (audienceId: number) => `/private/plays/audience/${audienceId}/suggest-name-tags`,
  voicePreview: '/private/plays/voice-preview',
  pauseSequence: (audienceId: number) => `/private/plays/audience/${audienceId}/sequence/pause`,
  sendNextStepNow: (audienceId: number) => `/private/plays/audience/${audienceId}/sequence/send-next-now`,
  resumeSequence: (audienceId: number) => `/private/plays/audience/${audienceId}/sequence/resume`,
  cancelSequence: (audienceId: number) => `/private/plays/audience/${audienceId}/sequence/cancel`,
  bulkCancelSequences: '/private/plays/audience/sequence/bulk-cancel',
  bulkCancelByCompany: '/private/plays/audience/sequence/cancel-by-company',
  removePlay: (audienceId: number) => `/private/plays/audiences/${audienceId}`,
  deleteCampaignFromAudience: (audienceId: number) => `/private/plays/audiences/${audienceId}/campaign`,
  checkPlayName: (playName: string) => `/private/audience/check-name/${playName}`,
  createAIAgent: '/private/plays/agents',
  getAIAgents: '/private/plays/agents',
  updateAIAgent: (id: number) => `/private/plays/agents/${id}`,
  getAIAgentById: (id: number) => `/private/plays/agents/${id}`,
  publishPlay: `/private/plays/publish`,
  getPlaysPhoneNumber: '/private/plays/phone-numbers',
  addPlaysPhoneNumber: '/private/plays/phone-numbers',
  getPlayResults: (id: number) => `/private/campaigns/audiences/${id}/results`,
  getRecentActivity: '/private/plays/runs/recent-activity',
  getRunHistory: (audienceId: number) => `/private/plays/runs/${audienceId}`,
  getRunDetails: (audienceId: number, runNumber: number) => `/private/plays/runs/${audienceId}/${runNumber}`,
  resumeExecutionSafely: (executionId: number) => `/private/plays/executions/${executionId}/resume-safe`,
  getHeyReachPendingStepLeadStatus: (audienceId: number, runNumber: number, audiencePlayId: number) =>
    `/private/plays/runs/${audienceId}/${runNumber}/heyreach-lead-status/${audiencePlayId}`,
  getPlayExecution: (id: number) => `/private/plays/executions/${id}`,
  getEmailThread: (sequenceId: string | number) => `/private/plays/email-thread/${sequenceId}`,
  getEmailDynamicVariables: '/private/plays/dynamic-variables',
  createEmailTemplate: '/private/plays/email-templates',
  updateEmailTemplate: (id: number) => `/private/plays/email-templates/${id}`,
  getEmailTemplates: '/private/plays/email-templates',
  uploadPlayImage: '/private/plays/upload-image',
  uploadPlayAudio: '/private/plays/upload-audio',
  uploadPlayAttachments: (playId: number) => `/private/plays/${playId}/attachments`,
  listPlayAttachments: (playId: number) => `/private/plays/${playId}/attachments`,
  deletePlayAttachment: (playId: number, attachmentId: number) => `/private/plays/${playId}/attachments/${attachmentId}`,
  getAttachmentDownloadUrl: (attachmentId: number) => `/private/plays/attachments/${attachmentId}/download-url`,
  getChatChannels: (platform: string) => `/private/plays/chat-channels/${platform}`,
  deleteAIAgent: (id: number) => `/private/plays/agents/${id}`,
  getSystemWorkflows: '/private/plays/system-workflows',
};

export const DIALER_API = {
  getWebCallToken: '/private/dialer/web-call-token',
  getCurrentProspect: (sessionId: string) => `/private/dialer/sessions/${sessionId}/current-prospect`,
  joinSessionAsObserver: (sessionId: string) => `/private/dialer/sessions/${sessionId}/join`,
  sendDTMF: '/private/dialer/send-dtmf',
  disconnectCustomer: '/private/dialer/disconnect-customer',
  disconnectProspect: '/private/dialer/disconnect-prospect',
  sdrDisconnect: (sessionId: string) => `/private/dialer/sessions/${sessionId}/sdr-disconnect`,
  reactivateSession: (sessionId: string) => `/private/dialer/sessions/${sessionId}/reactivate`,
  sessionHeartbeat: (sessionId: string) => `/private/dialer/sessions/${sessionId}/heartbeat`,
  sessionRecordings: (sessionId: string) => `/private/dialer/sessions/${sessionId}/recordings`,
  saveProspectNotes: (sessionId: string, prospectIndex: number) => `/private/dialer/sessions/${sessionId}/prospects/${prospectIndex}/notes`,
  // Full rendered email for an Add to Dialer prospect (epic #3561) — fetched
  // on selection only, never part of the 2s polling payload.
  getProspectEmailContext: (sessionId: string, prospectIndex: number) => `/private/dialer/sessions/${sessionId}/prospects/${prospectIndex}/email-context`,
  // PRD #2 (#2818): manual "Next call" advance in manual mode.
  advanceToNextProspect: (sessionId: string) => `/private/dialer/sessions/${sessionId}/advance`,
  // #5266: skip the current prospect in any advance mode. Unlike `advance`, this
  // does not require the manual wrap-up hold, so a stalled auto-mode session can
  // be moved on without waiting for a hold that will never arrive.
  skipCurrentProspect: (sessionId: string) => `/private/dialer/sessions/${sessionId}/skip`,
  singleCall: '/private/dialer/single-call',
  getSessionByAudience: (audienceId: number) => `/private/dialer/sessions/by-audience/${audienceId}`,
  directCall: '/private/dialer/direct-call',
  // Outside /dialer: that router refuses a workspace that cannot call; this says why.
  callingReadiness: '/private/calling-readiness',
  getDirectCall: (callId: string) => `/private/dialer/direct-calls/${callId}`,
  saveDirectCallNotes: (callId: string) => `/private/dialer/direct-calls/${callId}/notes`,
  endDirectCall: (callId: string) => `/private/dialer/direct-calls/${callId}/end`,
  updateCallRecording: (callSid: string) => `/private/dialer/calls/${callSid}/recording`,
  // #2197: sectioned website-screenshot panel. `sessionId` is in the PATH;
  // the target `url`, `labels`/`section`, and `bust` are query params. Base URL
  // already includes `/api`, so no `/api` prefix here.
  prospectScreenshot: (sessionId: string) => `/private/dialer/sessions/${sessionId}/prospect-screenshot`,
};

export const POWER_DIALER_API = {
  overview: '/private/power-dialer/sessions/overview',
  startSession: '/private/power-dialer/sessions',
  audienceCallPrep: (audienceId: number) =>
    `/private/power-dialer/audiences/${audienceId}/call-prep`,
  createPracticeCall: (sessionId: string) =>
    `/private/power-dialer/sessions/${encodeURIComponent(sessionId)}/practice-call`,
  getPracticeCall: (sessionId: string, callId: string) =>
    `/private/power-dialer/sessions/${encodeURIComponent(sessionId)}/practice-call/${encodeURIComponent(callId)}`,
  getSessionByExecutionAudience: (executionAudienceId: number) =>
    `/private/power-dialer/sessions/by-execution-audience/${executionAudienceId}`,
  getExclusionPreview: (listId: number) =>
    `/private/power-dialer/audiences/${listId}/exclusion-preview`,
  getSessionCalls: (executionAudienceId: number) =>
    `/private/power-dialer/sessions/${executionAudienceId}/calls`,
};

export const VOICEMAIL_TEMPLATES_API = {
  list: '/private/voicemail-templates',
  create: '/private/voicemail-templates',
  remove: (id: number) => `/private/voicemail-templates/${id}`,
};

const PHONE_NUMBERS_API = {
  getPhoneNumbers: '/private/phone-numbers',
  addPhoneNumber: '/private/phone-numbers',
  provisionPhoneNumber: '/private/phone-numbers/provision',
  togglePhoneNumberStatus: (id: number) => `/private/phone-numbers/${id}/status`,
  deletePhoneNumber: (id: number) => `/private/phone-numbers/${id}`,
};

const PERSONA_API = {
  generateTargetAudience: '/private/persona/generate-target-audience',
  createPersona: '/private/persona',
  getAllPersonas: '/private/persona/listings',
  deletePersona: (id?: number) => `/private/persona/${id}`,
  getPersonaDetails: (id?: number) => `/private/persona/details/${id}`,
  updatePersona: (id?: number) => `/private/persona/${id}`,
  getPersonaUsage: (id?: number) => `/private/persona/${id}/usage`,
};

const INTEGRATIONS_API = {
  getSalesforceFields: '/private/integration/fetch-salesforce-fields',
  getHubspotFields: '/private/integration/fetch-hubspot-fields',
  receiveCalendlyScript: '/private/integration/receive-calendly-script',
  getCalendarTimeSlots: '/public/integration/time-slots',
  getGoogleCalendarList: '/private/integration/google-calendars',
  selectGoogleCalendar: '/private/integration/select-google-calendar',
  addSalesForce: '/private/integration/initiate-salesforce-auth',
  addHubSpot: '/private/integration/initiate-hubspot-auth',
  authorizeHubSpotMarketplaceInstall: '/private/integration/hubspot/marketplace/authorize',
  finalizeHubSpotMarketplaceInstall: '/public/integration/hubspot-marketplace/finalize',
  disconnectHubSpot: '/private/integration/hubspot/disconnect',
  addApollo: '/private/integration/initiate-apollo-auth',
  // Sendoso (mb-server#6287). Per-rep connection: these are scoped to the
  // calling user, not the workspace, so two reps in one workspace each hold
  // their own.
  getSendosoStatus: '/private/integration/sendoso',
  addSendoso: '/private/integration/sendoso/authorize',
  disconnectSendoso: '/private/integration/sendoso',
  // Goody (mb-server Goody MVP). ONE connection per workspace, connected by an
  // admin with a Goody API key; GET is open to every member, never returns the key.
  goodyIntegration: '/private/integration/goody',
  tremendousIntegration: '/private/integration/tremendous',
  addTremendous: '/private/integration/tremendous/authorize',
  getPublicCalendarData: (slug: string) => `/public/integration/meeting/book/me/${slug}`,
  getIntegrationsByChatToken: (chatToken: string) =>
    `/private/integration/get-integrations-by-chat-token/${chatToken}`,
  listIntegrations: '/private/integration/list-integrations',
  bookMeeting: '/public/integration/book-meeting',
  getEmailIntegrations: '/private/integration/list-email-integrations',
  toggleEmailIntegration: (authId: number, platform: string) =>
    `/private/integration/email/${authId}/${platform}/toggle`,
  deleteEmailIntegration: (authId: number, platform: string) =>
    `/private/integration/email/${authId}/${platform}`,
  testEmailConnection: (authId: number, platform: string) =>
    `/private/integration/email/${authId}/${platform}/test`,
  updateMailboxSettings: '/private/integration/update-mailbox-settings',
  // Calendar endpoints
  getCalendarIntegrations: '/private/integration/list-calendar-integrations',
  toggleCalendarIntegration: (authId: number, platform: string) =>
    `/private/integration/calendar/${authId}/${platform}/toggle`,
  deleteCalendarIntegration: (authId: number, platform: string) =>
    `/private/integration/calendar/${authId}/${platform}`,
  testCalendarConnection: (authId: number, platform: string) =>
    `/private/integration/calendar/${authId}/${platform}/test`,
  saveHubSpotMappings: '/private/integration/save-hubspot-field-mappings',
  getHubSpotAuthStatus: '/private/integration/hubspot-auth-status',
  getSalesforceAuthStatus: '/private/integration/salesforce-auth-status',
  getHubSpotFieldMappings: '/private/integration/get-hubspot-field-mappings',
  saveHubSpotMappingsEnhanced: '/private/integration/save-hubspot-field-mappings-enhanced',
  suggestFieldMappings: '/private/integration/suggest-field-mappings',
  getHubSpotFieldMappingsEnhanced: '/private/integration/get-hubspot-field-mappings-enhanced',
  saveSalesforceMappings: '/private/integration/save-salesforce-field-mappings',
  getSalesforceFieldMappings: '/private/integration/get-salesforce-field-mappings',
  salesforcePersonObjectMode: '/private/integration/salesforce-person-object-mode',
  salesforceCanvas: '/private/integration/salesforce-canvas',
  salesforceStats: '/private/integration/salesforce-stats',
  salesforceAuth: '/private/integration/salesforce-auth',
  addLinkedin: '/private/integration/initiate-linkedin-auth',
  getWebhook: (platform: string) => `/private/webhooks/${platform}`,
  generateWebhook: (platform: string) => `/private/webhooks/${platform}/generate`,
  getAllCalendarsByUser: '/private/integration/get-all-calendars-by-user',
  getAllEmailAccountsByUser: '/private/integration/get-all-email-accounts-by-user',
  addNotion: '/private/integration/initiate-notion-auth',
  getNotionDatabasesAndPages: '/private/integration/get-notion-databases-and-pages',
  saveSelectedNotionPages: '/private/dam/notion/pages',
  getSelectedNotionPages: '/private/dam/notion/pages',
  addBynder: '/private/dam/bynder/auth',
  getSelectedNotionPagesWithStatus: '/private/dam/notion/pages/status',
  trainNotionPages: '/private/dam/notion/train',
  getBynderCollections: '/private/dam/bynder/collections',
  saveSelectedBynderCollections: '/private/dam/bynder/collections',
  getSelectedBynderCollections: '/private/dam/bynder/selected-collections',
  getSelectedBynderCollectionsWithStatus: '/private/dam/bynder/collections/status',
  trainBynderCollections: '/private/dam/bynder/train',
  getBynderAssets: '/private/dam/bynder/assets',
  saveSelectedBynderAssets: '/private/dam/bynder/assets',
  getSelectedBynderAssets: '/private/dam/bynder/selected-assets',
  getBynderAssetsWithStatus: '/private/dam/bynder/assets/status',
  addHeyGen: '/private/integration/initiate-heygen-auth',
  addGoogleSheets: '/private/integration/initiate-google-sheets-auth',
  addGoogleDrive: '/private/integration/initiate-google-drive-auth',
  addVector: '/private/integration/initiate-vector-auth',
  getVectorSettings: '/private/integration/vector/settings',
  updateVectorSettings: '/private/integration/vector/settings',
  getGoogleSheetsAuthId: '/private/integration/auth-id',
  getHeyReachCampaigns: '/private/integration/heyreach/campaigns',
  getHeyReachAccounts: '/private/integration/heyreach/accounts',
  getHeyReachProxyCountries: '/private/integration/heyreach/accounts/proxy-countries',
  connectHeyReachAccount: '/private/integration/heyreach/accounts/connect',
  connectHeyReachAccountWithCookies: '/private/integration/heyreach/accounts/connect-with-cookies',
  getHeyReachAccountStatus: (accountId: number) => `/private/integration/heyreach/accounts/${accountId}/status`,
  submitHeyReachAccountPin: (accountId: number) => `/private/integration/heyreach/accounts/${accountId}/pin`,
  reconnectHeyReachAccount: (accountId: number) => `/private/integration/heyreach/accounts/${accountId}/reconnect`,
  resyncHeyReachAccount: (accountId: number) => `/private/integration/heyreach/accounts/${accountId}/resync`,
  reloginHeyReachSalesNavigator: (accountId: number) => `/private/integration/heyreach/accounts/${accountId}/relogin-sales-navigator`,
  reloginHeyReachRecruiter: (accountId: number) => `/private/integration/heyreach/accounts/${accountId}/relogin-recruiter`,
  getHeyReachAccountProxy: (accountId: number) => `/private/integration/heyreach/accounts/${accountId}/proxy`,
  updateHeyReachAccountInbox: (accountId: number) => `/private/integration/heyreach/accounts/${accountId}/inbox-configuration`,
  removeHeyReachAccount: (accountId: number) => `/private/integration/heyreach/accounts/${accountId}`,
  // Countries a seat may CONTACT. Unrelated to getHeyReachAccountProxy above, which is
  // the country the seat LOGS IN from — the two were deliberately kept apart.
  getHeyReachOutreachRegions: '/private/integration/heyreach/accounts/outreach-regions',
  updateHeyReachOutreachRegion: (accountId: number) => `/private/integration/heyreach/accounts/${accountId}/outreach-regions`,
  getHeyReachStatus: '/private/integration/heyreach/status',
  // MarketBetter-managed default delivery campaign (#2004) — GET reads state,
  // POST idempotently ensures/creates it.
  heyReachDefaultCampaign: '/private/integration/heyreach/default-campaign',
  saveHeyReachApiKey: '/private/integration/heyreach/api-key',
  removeHeyReachApiKey: '/private/integration/heyreach/api-key',
  // HeyReach agency onboarding (managed workspace under MB agency org)
  postHeyReachAgencyOnboarding: '/private/integration/heyreach/agency/onboarding',
  getHeyReachAgencyOnboardingStatus: '/private/integration/heyreach/agency/onboarding/status',
  deleteHeyReachAgencyOnboarding: '/private/integration/heyreach/agency/onboarding',
  postHeyReachAgencyInviteMember: '/private/integration/heyreach/agency/invite-member',
};

const WORKFLOW_API = {
  queryWorkflow: '/private/workflows/stream',
};

const CONVERSATIONS_API = {
  createConversation: '/private/conversations',
  getConversation: (id: string) => `/private/conversations/${id}`,
};

const FEEDBACK_API = {
  feedbacks: '/private/feedback/requests',
  updateFeedback: (id: string) => `/private/feedback/requests/${id}`,
  voteFeedback: (id: string) => `/private/feedback/requests/${id}/vote`,
  categories: '/private/feedback/categories',
};

const TOKENS_API = {
  createApiToken: '/private/tokens',
};

const WORKSPACE_API = {
  // Core workspace operations
  listWorkspaces: '/private/workspaces',
  getActiveWorkspace: '/private/workspaces/active',
  createWorkspace: '/private/workspaces',
  getWorkspace: (id: number) => `/private/workspaces/${id}`,
  updateWorkspace: (id: number) => `/private/workspaces/${id}`,
  deleteWorkspace: (id: number) => `/private/workspaces/${id}`,

  // Workspace switching
  switchWorkspace: '/private/workspaces/switch',
  joinWorkspace: '/private/workspaces/join',

  // Member management
  getWorkspaceMembers: (id: number) => `/private/workspaces/${id}/members`,
  getWorkspaceRoles: (id: number) => `/private/workspaces/${id}/roles`,
  createWorkspaceRole: (id: number) => `/private/workspaces/${id}/roles`,
  updateWorkspaceRole: (id: number, roleId: string) => `/private/workspaces/${id}/roles/${roleId}`,
  deleteWorkspaceRole: (id: number, roleId: string) => `/private/workspaces/${id}/roles/${roleId}`,
  assignMemberRole: (id: number, memberId: string) => `/private/workspaces/${id}/members/${memberId}/role`,
  updateMemberUiPersona: (id: number, memberId: string) =>
    `/private/workspaces/${id}/members/${memberId}/ui-persona`,
  updateMyTaskDraftLanguage: (id: number) =>
    `/private/workspaces/${id}/members/me/task-draft-language`,
  updateMemberTaskDraftLanguage: (id: number, memberId: string) =>
    `/private/workspaces/${id}/members/${memberId}/task-draft-language`,
  updateMemberPermissions: (id: number, memberId: string) =>
    `/private/workspaces/${id}/members/${memberId}`,
  removeMember: (id: number, memberId: string) => `/private/workspaces/${id}/members/${memberId}`,
  // requestCreditAccess removed (#2711): credit eligibility is now role-based.

  // Admin management (Multi-Admin feature)
  promoteToAdmin: (workspaceId: number, userId: string) =>
    `/private/workspaces/${workspaceId}/admins/${userId}`,
  demoteFromAdmin: (workspaceId: number, userId: string) =>
    `/private/workspaces/${workspaceId}/admins/${userId}`,
  disableMemberMfa: (workspaceId: number, memberId: string) =>
    `/private/workspaces/${workspaceId}/members/${memberId}/disable-mfa`,

  // Join code management
  generateJoinCode: (workspaceId: number) => `/private/workspaces/${workspaceId}/generate-join-code`,

  // Credit management (all require workspace ID in path)
  getWorkspaceCredits: (id: number) => `/private/workspaces/${id}/credits`,
  getCreditAnalytics: (id: number) => `/private/workspaces/${id}/credits/analytics`,
  getCreditHistory: (id: number) => `/private/workspaces/${id}/credits/history`,

  // Email unsubscribes
  getEmailUnsubscribes: (id: number) => `/private/workspaces/${id}/email-unsubscribes`,
  deleteEmailUnsubscribe: (workspaceId: number, id: number) =>
    `/private/workspaces/${workspaceId}/email-unsubscribes/${id}`,

  // Invitations
  sendInvitation: '/private/workspaces/invite',
  sendInvitationToWorkspace: (id: number) => `/private/workspaces/${id}/invite`,
  getInvitations: '/private/workspaces/invitations',
  getWorkspaceInvitations: (id: number) => `/private/workspaces/${id}/invitations`,
  revokeInvitation: (invitationId: number) => `/private/workspaces/invitations/${invitationId}`,
  resendInvitation: (invitationId: number) =>
    `/private/workspaces/invitations/${invitationId}/resend`,
  acceptInvitation: (token: string) => `/private/workspaces/invite/${token}/accept`,

  // Company research
  triggerCompanyResearch: (id: number) => `/private/workspaces/${id}/research`,
  getCompanyResearch: (id: number) => `/private/workspaces/${id}/research`,

  // Cold call context management
  getColdCallContext: (id: number) => `/private/workspaces/${id}/cold-call-context`,
  updateColdCallContext: (id: number) => `/private/workspaces/${id}/cold-call-context`,
  deleteColdCallContext: (id: number) => `/private/workspaces/${id}/cold-call-context`,

  // Job posting context management
  triggerJobPostingContextGeneration: (id: number) => `/private/workspaces/${id}/job-posting-context/generate`,

  // Job titles generation
  generateJobTitles: (id: number) => `/private/workspaces/${id}/job-titles/generate`,

  // Technologies generation
  generateTechnologies: (id: number) => `/private/workspaces/${id}/technologies/generate`,

  // Signal configs generation (generates 2-3 complete configs)
  generateSignalConfigs: (id: number) => `/private/workspaces/${id}/signal-configs/generate`,

  // Market intel config generation (focus description + tags)
  generateMarketIntelConfig: (id: number) => `/private/workspaces/${id}/market-intel-config/generate`,

  // Community mentions config generation (keywords + subreddits)
  generateCommunityMentionsConfig: (id: number) => `/private/workspaces/${id}/community-mentions-config/generate`,

  // Personas generation (auto-generate from company research)
  generatePersonas: (id: number) => `/private/workspaces/${id}/personas/generate`,
};

const TEMPLATES_API = {
  createTemplate: '/private/templates',
  updateTemplate: (templateId: string) => `/private/templates/${templateId}`,
  deleteTemplate: (templateId: string) => `/private/templates/${templateId}`,
  getTemplate: (templateId: string) => `/private/templates/${templateId}`,
  listTemplates: '/private/templates',
  renderTemplate: (templateId: string) => `/private/templates/${templateId}/render`,
};

const AI_SEO_API = {
  getDashboard: (vendorid: number) => `/private/ai-seo/vendors/${vendorid}/dashboard`,
  getPrompts: (vendorid: number) => `/private/ai-seo/vendors/${vendorid}/prompts`,
  getResponses: (vendorid: number) => `/private/ai-seo/vendors/${vendorid}/responses`,
  getContentBriefs: (vendorid: number) => `/private/ai-seo/vendors/${vendorid}/content-briefs`,
  getAnalysis: (vendorid: number) => `/private/ai-seo/vendors/${vendorid}/analysis`,
  generatePrompts: (vendorid: number) => `/private/ai-seo/vendors/${vendorid}/generate-prompts`,
  collectResponses: (vendorid: number) => `/private/ai-seo/vendors/${vendorid}/collect-responses`,
  runAnalysis: (vendorid: number) => `/private/ai-seo/vendors/${vendorid}/run-analysis`,
  getStatus: (vendorid: number) => `/private/ai-seo/vendors/${vendorid}/status`,
  gscStatus: (vendorid: number) => `/private/ai-seo/vendors/${vendorid}/gsc/status`,
  gscSync: (vendorid: number) => `/private/ai-seo/vendors/${vendorid}/gsc/sync`,
  gscOverview: (vendorid: number) => `/private/ai-seo/vendors/${vendorid}/gsc/overview`,
  gscQueries: (vendorid: number) => `/private/ai-seo/vendors/${vendorid}/gsc/queries`,
  gscPages: (vendorid: number) => `/private/ai-seo/vendors/${vendorid}/gsc/pages`,
  gscStrikingDistance: (vendorid: number) => `/private/ai-seo/vendors/${vendorid}/gsc/striking-distance`,
  gscDeclining: (vendorid: number) => `/private/ai-seo/vendors/${vendorid}/gsc/declining`,
  gscDisconnect: (vendorid: number) => `/private/ai-seo/vendors/${vendorid}/gsc/disconnect`,
  initiateGscAuth: '/private/integration/initiate-google-search-console-auth',
  createPrompt: (vendorid: number) => `/private/ai-seo/vendors/${vendorid}/prompts`,
  updatePrompt: (vendorid: number, promptId: string) => `/private/ai-seo/vendors/${vendorid}/prompts/${promptId}`,
  deletePrompt: (vendorid: number, promptId: string) => `/private/ai-seo/vendors/${vendorid}/prompts/${promptId}`,
  archivePrompts: (vendorid: number) => `/private/ai-seo/vendors/${vendorid}/prompts/archive`,
};

const CONVERSATION_FLOW_API = {
  listConversationFlows: '/private/conversation-flows',
  getConversationFlow: (id: number) => `/private/conversation-flows/${id}`,
  createConversationFlow: '/private/conversation-flows',
  updateConversationFlow: (id: number) => `/private/conversation-flows/${id}`,
  deleteConversationFlow: (id: number) => `/private/conversation-flows/${id}`,
};

// System Jobs API (Super Admin)
export const SYSTEM_JOBS_API = {
  getJobs: '/admin/jobs',
  getJobNames: '/admin/jobs/names',
  getJobStatus: (jobName: string) => `/admin/jobs/${jobName}`,
  runJob: (jobName: string) => `/admin/jobs/${jobName}/run`,
  updateJobSettings: (jobName: string) => `/admin/jobs/${jobName}`,
  resetJobSettings: (jobName: string) => `/admin/jobs/${jobName}/settings`,
};

// System Stats API (Super Admin)
export const SYSTEM_STATS_API = {
  lastHour: '/admin/system-stats/last-hour',
};

// User email signatures across workspaces (Super Admin)
export const ADMIN_PIPELINE_HEALTH_API = {
  overview: '/admin/pipeline-health',
};

// Pounce notification metrics across workspaces (Super Admin, #4544)
export const ADMIN_POUNCE_METRICS_API = {
  overview: '/admin/pounce-metrics',
};

export const ADMIN_USER_SIGNATURES_API = {
  list: '/admin/user-signatures',
  vendors: '/admin/user-signatures/vendors',
};

// SDR Learning API (Super Admin) — what the AI has learned about each SDR (#1829)
export const ADMIN_SDR_LEARNING_API = {
  list: '/admin/sdr-learnings',
  summary: '/admin/sdr-learnings/summary',
  vendors: '/admin/sdr-learnings/vendors',
  detail: (userId: string) => `/admin/sdr-learnings/${userId}`,
  refresh: (userId: string) => `/admin/sdr-learnings/${userId}/refresh`,
  sentEmails: (userId: string) => `/admin/sdr-learnings/${userId}/emails`,
};

// Announcements API (Super Admin) — author/broadcast in-app feature announcements (#3425, #3430)
export const ADMIN_ANNOUNCEMENTS_API = {
  list: '/admin/announcements',
  create: '/admin/announcements',
  audienceCount: '/admin/announcements/audience-count',
  poster: '/admin/announcements/poster',
  detail: (id: number) => `/admin/announcements/${id}`,
  update: (id: number) => `/admin/announcements/${id}`,
  remove: (id: number) => `/admin/announcements/${id}`,
  stats: (id: number) => `/admin/announcements/${id}/stats`,
};

// Announcements API (vendor-facing) — active feed, panel, dismiss, seen, CTA click (#3425, #3430)
export const ANNOUNCEMENTS_API = {
  active: '/private/announcements',
  panel: '/private/announcements/panel',
  unreadCount: '/private/announcements/unread-count',
  markAllRead: '/private/announcements/mark-all-read',
  dismiss: (id: number) => `/private/announcements/${id}/dismiss`,
  ctaClick: (id: number) => `/private/announcements/${id}/cta-click`,
};

// Feature Enablement API (Super Admin)
export const FEATURE_ENABLEMENT_API = {
  getVendors: '/admin/feature-enablement/vendors',
  getVendor: (vendorId: number) => `/admin/feature-enablement/vendors/${vendorId}`,
  updateVendor: (vendorId: number) => `/admin/feature-enablement/vendors/${vendorId}`,
  getModelOptions: '/admin/feature-enablement/model-options',
  updateModelSettings: (vendorId: number) => `/admin/feature-enablement/vendors/${vendorId}/model-settings`,
  updateCompanyRevealBalance: (vendorId: number) => `/admin/feature-enablement/vendors/${vendorId}/company-reveal-balance`,
};

// Slack User Mapping API (Super Admin)
export const SLACK_USER_MAPPING_API = {
  getMappings: '/admin/slack-user-mappings',
  getVendors: '/admin/slack-user-mappings/vendors',
  getVendorUsers: (vendorId: number) => `/admin/slack-user-mappings/users/${vendorId}`,
  getSlackUsers: (vendorId: number) => `/admin/slack-user-mappings/slack-users/${vendorId}`,
  getStats: '/admin/slack-user-mappings/stats',
  createMapping: '/admin/slack-user-mappings',
  deleteMapping: (id: number) => `/admin/slack-user-mappings/${id}`,
};

// Teams User Mapping API (Super Admin)
export const TEAMS_USER_MAPPING_API = {
  getMappings: '/admin/teams-user-mappings',
  getVendors: '/admin/teams-user-mappings/vendors',
  getVendorUsers: (vendorId: number) => `/admin/teams-user-mappings/users/${vendorId}`,
  getTeamsUsers: (vendorId: number) => `/admin/teams-user-mappings/teams-users/${vendorId}`,
  getStats: '/admin/teams-user-mappings/stats',
  createMapping: '/admin/teams-user-mappings',
  deleteMapping: (id: number) => `/admin/teams-user-mappings/${id}`,
};

// System Workflows Admin API (Super Admin)
export const SYSTEM_WORKFLOWS_ADMIN_API = {
  list: '/admin/system-workflows',
  create: '/admin/system-workflows',
  update: (id: number) => `/admin/system-workflows/${id}`,
  toggle: (id: number) => `/admin/system-workflows/${id}/toggle`,
  delete: (id: number) => `/admin/system-workflows/${id}`,
};

export const SYSTEM_AGENTS_ADMIN_API = {
  list: '/admin/system-agents',
  create: '/admin/system-agents',
  update: (id: number) => `/admin/system-agents/${id}`,
  delete: (id: number) => `/admin/system-agents/${id}`,
};

// Signal Scout — per-vendor custom signal agents (super-admin). No DELETE route
// exists on the backend; lifecycle end = disable (enabled=false).
export const SIGNAL_AGENTS_ADMIN_API = {
  listByVendor: (vendorId: number) => `/admin/signal-agents/vendors/${vendorId}`,
  createForVendor: (vendorId: number) => `/admin/signal-agents/vendors/${vendorId}`,
  update: (id: number) => `/admin/signal-agents/${id}`,
  run: (id: number) => `/admin/signal-agents/${id}/run`,
};

// Admin Signals API (Super Admin)
export const ADMIN_SIGNALS_API = {
  list: '/admin/signals',
  stats: '/admin/signals/stats',
  detail: (id: string) => `/admin/signals/${id}`,
  vendors: '/admin/signals/vendors',
  signalTypes: '/admin/signals/signal-types',
};

export const ADMIN_CRM_SYNC_API = {
  overview: '/admin/crm-sync/overview',
};

// OAuth API endpoints - "Login with Google" and "Login with Microsoft"
const OAUTH_API = {
  // Public endpoint - check if email is OAuth-only user
  checkEmail: (email: string) => `/public/users/oauth-check?email=${encodeURIComponent(email)}`,
  // Private endpoints - require auth
  getStatus: '/private/users/oauth/status',
  link: '/private/users/oauth/link',
};

export const API_ENDPOINTS = {
  /** Remaining credits of every connected bring-your-own enrichment provider (mb-server#6196). */
  enrichmentCredits: '/private/integration/enrichment-credits',
  LOGIN: '/api/internal/auth/login',
  SIGNUP: '/api/internal/auth/sign-up',
  LOGOUT: '/api/internal/auth/logout',
  SEND_OTP: '/api/internal/auth/send-otp',
  VERIFY_OTP: '/api/internal/auth/verify-otp',
  CREATE_USER: '/private/users/',
  GET_LEADS: '/private/vendors/vendor-leads',
  getAskIotUserDetails: '/private/users/api/',
  createAskIotUser: '/private/users/',
  createUnityUser: '/public/users/create-unity-user',
  createStandaloneUser: '/public/users/create-standalone-user',

  updateUserCalendarSettings: '/private/users/calendar-settings',
  updateTimezonePreference: '/private/users/timezone-preference',
  detectTimezonePreference: '/private/users/timezone-preference/detect',
  getUserSignature: '/private/users/signature',
  updateUserSignature: '/private/users/signature',
  syncGmailSignature: '/private/users/signature/sync-gmail',
  listGmailSendAs: '/private/users/signature/send-as-list',
  listGmailAccounts: '/private/users/signature/gmail-accounts',
  getUserVoicemail: '/private/users/voicemail',
  // #4360: the signed-in rep's own enrolled voice. Self-scoped server-side —
  // there is no path variant that addresses another user.
  userVoiceProfile: '/private/users/voice-profile',
  updateUserVoicemail: '/private/users/voicemail',
  getSdrVoice: '/private/users/sdr-voice',
  updateSdrVoice: '/private/users/sdr-voice',
  getPendingInvitations: '/private/users/pending-invitations',
  autoAcceptInvitations: '/private/users/auto-accept-invitations',
  REQUEST_EARLY_ACCESS: '/public/users/request-early-access',
  ...IOT_GPT_API,
  ...PRODUCT_API,
  ...QUOTES_API,
  ...SUBSCRIPTION_API,
  ...PROJECTS_API,
  ...VENDOR_API,
  ...ANALYTICS_API,
  createUserSuggestedIdea: '/private/content/create-suggested-idea',
  updateLinkOptions: '/private/links/link-options-bulk',
  updateSingleLinkOptions: '/private/links/link-options',
  updateAiSuggestions: '/private/links/ai-suggestions',
  deleteAiSuggestions: (linkId: string | number) => `/private/links/ai-suggestions/${linkId}`,
  regenerateAiSuggestions: '/private/links/ai-suggestions/regenerate',
  uploadLinkFile: '/private/links/upload-file',
  getVendorId: '/private/chatbot/vendor-id',
  getDashboardScore: '/private/chatbot/get-dashboard-stats',
  getHelpSection: (token: string) => `/public/chatbot/help-section/${token}`,
  getBotBlog: (token: string) => `/public/chatbot/highlighted-page/${token}`,
  getBotWebinar: (token: string) => `/public/chatbot/webinar/${token}`,
  getDemoDetails: (token: string) => `/public/chatbot/demo-details/${token}`,
  // GH#6239: the admin app reads its own bot through the authenticated door. The public
  // `/public/chatbot/get/:token` strips `allowed_countries` and 404s outside the allowlist —
  // correct for visitors, and the cause of both a silently wiped country list and an admin
  // lockout when the admin surfaces used it.
  getBotData: (token: string) => `/private/chatbot/admin-config/${token}`,
  getLLMSetting: (token: string) => `/private/chatbot/advanced-settings/${token}`,
  listPersonas: (token: string) => `/private/chatbot/personas/${token}`,
  getLinks: `/private/links/list-links`,
  getTrainedData: `/private/training/get`,
  getVideoLinks: (token: string) => `/private/links/list-links?chat_token=${token}&type=video`,
  getThreadDetails: '/private/chatbot/threads/vendor',
  liveVisitors: '/private/live-visitors',
  pounceLiveVisitor: (visitorId: string) => `/private/live-visitors/${visitorId}/pounce`,
  dismissLiveVisitor: (visitorId: string) => `/private/live-visitors/${visitorId}/dismiss`,
  removeTrainedData: (file_id: number) => `/private/chatbot/delete-file/${file_id}`,
  generateOutline: '/private/content/content-interaction',
  generateImages: '/private/content/generate-images',
  enhanceArticle: '/private/content/enhance-article',
  getArchive: '/private/content/markInteractionInactive',
  generateCloudFlareUploadVideoUrl: '/private/links/generate-upload-url',
  validateEmail: '/public/users/validate-email',
  convertDiscoveredLink: (linkId: number) => `/private/links/convert-discovered/${linkId}`,
  siteDiscovery: '/private/links/discovery',
  ...WORKFLOW_API,
  ...CONVERSATIONS_API,
  ...FEEDBACK_API,
  ...INTEGRATIONS_API,
  ...VOICE_API,
  ...BOT_API,
  ...AUDIENCES_API,
  ...PLAYS_API,
  ...NOTIFICATION_POLICIES_API,
  ...PHONE_NUMBERS_API,
  ...PERSONA_API,
  ...TEMPLATES_API,
  ...TOKENS_API,
  ...WORKSPACE_API,
  ...AI_SEO_API,
  ...PROSPECTS_API,
  ...OPPORTUNITIES_API,
  ...CONVERSATION_FLOW_API,
  ...DASHBOARD_API,
  ...INTEGRATIONS_CONTACT_API,

  // HeyGen integration
  heygenAvatars: '/private/integration/heygen/avatars',
  heygenVoices: '/private/integration/heygen/voices',
  heygenAvatarGroups: '/private/integration/heygen/avatar-groups',
  heygenAvatarsInGroup: '/private/integration/heygen/avatar-group',
  heygenUploadAsset: '/private/integration/heygen/assets/upload',
  heygenApiToken: '/private/integration/heygen/api-token',
  heygenApiTokenStatus: '/private/integration/heygen/api-token/status',

  // SixSense integration
  sixsenseApiToken: '/private/integration/sixsense/api-token',
  sixsenseApiTokenStatus: '/private/integration/sixsense/api-token/status',

  // Exa integration
  exaApiKey: '/private/integration/exa/api-key',
  exaAuthStatus: '/private/integration/exa/auth-status',

  // LinkedIn Share integration
  linkedinShareAuthStatus: '/private/integration/linkedin/share/auth-status',
  linkedinPosting: '/private/integration/linkedin/share/automatic-posting',
  linkedinPosts: '/private/integration/linkedin/posts',

  // Customer Rules
  customerRules: '/private/customer-rules/rules',
  customerRuleCrmFields: '/private/customer-rules/crm-fields',
  customerRulePreview: '/private/customer-rules/preview',
  customerRuleSync: '/private/customer-rules/sync',
  customerRule: (id: string) => `/private/customer-rules/rules/${id}`,

  // Agent Types
  agentTypes: '/private/agent-types',
  agentType: (id: string) => `/private/agent-types/${id}`,
  configureAgentType: (id: string) => `/private/agent-types/${id}/configure`,

  // Integration Usage
  integrationUsage: '/private/integration/usage',

  // OAuth
  oauthCheckEmail: (email: string) => `/public/users/oauth-check?email=${encodeURIComponent(email)}`,
  oauthStatus: '/private/users/oauth/status',
  oauthLink: '/private/users/oauth/link',

  // SSO
  checkSso: '/public/auth/check-sso',
  userAuthType: '/public/auth/user-auth-type',

  // AI Search
  aiSearchPreview: '/private/ai-search/preview',
  aiSearchImport: '/private/ai-search/import',
  aiSearchContacts: '/private/ai-search/contacts',

  // AI Context files (markdown knowledge base — mb-server#3535)
  contextFiles: '/private/context-files',
  contextFile: (id: string) => `/private/context-files/${id}`,
  contextFileVersions: (id: string) => `/private/context-files/${id}/versions`,
  contextFileVersion: (id: string, versionId: string) =>
    `/private/context-files/${id}/versions/${versionId}`,
  contextFileImprove: (id: string) => `/private/context-files/${id}/improve`,
  contextFileFixIssues: (id: string) => `/private/context-files/${id}/fix-issues`,
  contextFilesUpload: '/private/context-files/upload',
  contextFilesImportUrl: '/private/context-files/import-url',
  contextFileDrafts: '/private/context-files/drafts',
  contextFileDraftApply: (id: string) => `/private/context-files/drafts/${id}/apply`,
  contextFileDraftDismiss: (id: string) => `/private/context-files/drafts/${id}/dismiss`,
  contextFilesGenerate: '/private/context-files/generate',
  contextFilesTest: '/private/context-files/test',
  contextFilesTestSuggestions: '/private/context-files/test-suggestions',
  contextFileGaps: '/private/context-files/gaps',
  contextFileGapFix: (id: string) => `/private/context-files/gaps/${id}/fix`,
  gbrainIntegration: '/private/integration/gbrain',
  gbrainSource: '/private/integration/gbrain/source',
  zoomInfoIntegration: '/private/integration/zoominfo',
  zoomInfoAuthorize: '/private/integration/zoominfo/authorize',
  zoomInfoTest: '/private/integration/zoominfo/test',

  // Misc
  marketIntelConfig: '/private/market-intel/config',
  jobPostingConfigs: '/private/job-posting-configs',
  f5botConfigs: '/private/f5bot-configs',
  linkedinMentionConfigs: '/private/linkedin-mention-configs',
  communityMentions: '/private/analytics/community-mentions',
};
